import {
  SoftSurveyComplete,
  SoftSurveyIntro,
  SoftSurveyQuestion,
} from '@chiroup/components/survey/soft';
import { surveyIds } from '@chiroup/core/constants/SurveyIds';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import {
  SurveyProgress,
  SurveyQuestion,
  SurveyQuestionAnswer,
} from '@chiroup/core/types/Survey.type';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';
import requestService from '../../services/request.service';
import InterCCS from './InterCCS';
import InterReeval from './InterReeval';

type Status = 'intro' | 'question' | 'complete';

type Props = {
  requestId: string;
  validationCode: string | number;
  patientSurveyId: string;
  // The survey shape from the legacy bootstrap (`SurveyHealthcom`):
  // `currentQuestion` / `previousQuestion` / `progress` / `started`
  // are precomputed by the server and `questions` is intentionally
  // undefined for non-custom-behavesAs surveys (so the client can't
  // skip ahead). The Cognito-authed flow can also pass a full
  // `questions` array if it wants to drive locally.
  survey: {
    id?: string;
    title?: string;
    subtitle?: string;
    startMessage?: string;
    endMessage?: string;
    questions?: SurveyQuestion[];
    currentQuestion?: SurveyQuestion;
    previousQuestion?: string;
    progress?: SurveyProgress;
    started?: boolean;
    // The CCS / reevaluation flow uses a body picker + chained
    // follow-ups that the soft-card UI can't represent. When this is
    // 'ccs' / 'reevaluation' / 'abbreviatedCCS' we delegate the
    // entire survey to the legacy `<ChiefComplaintSurvey>` via
    // `<InterCCS>`. Otherwise the standard per-question runner.
    behavesAs?: string | null;
    allowAdditionalComplaints?: boolean;
  };
  language: 'en' | 'es';
  primaryColor: string;
  prMap?: { [key: string]: string };
  encounterId?: string;
  kiosk?: boolean;
  remainingSteps?: number[];
  onComplete: () => void;
  alreadyComplete?: boolean;
  // Patient context surfaced by the bootstrap (`requestGetAuth`) and
  // forwarded through `RequestRunner`. CCS needs gender + age to pick
  // the right anatomy / question set.
  gender?: Genders;
  age?: number;
  ccsFollowups?: string[];
  surveyIds?: string[];
  // Patient's existing complaints — required by the reevaluation flow
  // so <ReevalSurvey> can pre-populate per-complaint follow-up
  // questions. Optional because non-reeval surveys don't need it.
  complaints?: any[];
};

/**
 * `/requests/:id` survey step — wraps the same Soft Card panels used by
 * the Cognito-authed runner but drives them with the
 * validationCode-keyed public endpoints (the legacy
 * `requestSurveyAnswerSubmit` / `requestSurveyGetQuestion` Lambdas).
 */
const StepSurvey: React.FC<Props> = ({
  requestId,
  validationCode,
  patientSurveyId,
  survey,
  language,
  primaryColor,
  prMap,
  encounterId,
  kiosk,
  remainingSteps,
  onComplete,
  alreadyComplete,
  gender,
  age,
  ccsFollowups,
  surveyIds: surveyIdsProp,
  complaints,
}) => {
  // Reeval is detected by `behavesAs === 'reevaluation'` OR by the
  // standard reeval survey id from the constants table — the schedule
  // backend currently returns the latter without setting behavesAs on
  // the bootstrap response (see kioskRequestGet / requestGetAuth).
  // Reeval owns the entire surface but it is NOT a CCS-like flow — it
  // pre-populates per-complaint follow-up questions from the patient's
  // existing complaints and only branches into a bodypicker when the
  // patient answers "Yes" to the new-problems prompt. <ReevalSurvey>
  // (via <InterReeval>) is the only component that implements that
  // chain; <ChiefComplaintSurvey> via <InterCCS> would just submit the
  // single new-problems answer and walk away — which is the bug that
  // sent us here.
  const isReeval = survey.behavesAs === 'reevaluation' || survey.id === surveyIds.reevaluation;
  // CCS-like surveys always start with a bodypicker. We detect them
  // either by the explicit `behavesAs` flag from the bootstrap or by
  // inspecting the current question's type. Reeval is explicitly
  // excluded here because the reeval start question is a radio, not a
  // bodypicker, and routing reeval to InterCCS skips all the
  // per-complaint follow-ups.
  const ccsLikeBehavesAs = survey.behavesAs === 'ccs' || survey.behavesAs === 'abbreviatedCCS';
  const startQuestion = survey.currentQuestion ?? survey.questions?.[0];
  const isCCSLike = !isReeval && (ccsLikeBehavesAs || startQuestion?.type === 'bodypicker');
  // Server pre-computes `currentQuestion` for kiosk + `/requests`
  // bootstraps. If the caller supplied a full `questions[]` (Cognito
  // flow loading the whole survey), fall back to the first
  // unanswered question. Either way, this is the question the
  // runner lands on when the patient hits "Get started".
  const initialQuestions = (survey.questions || []).filter(Boolean);
  const fallbackUnanswered = initialQuestions.find(q => !q.answer) ?? null;
  const initialCurrent = survey.currentQuestion ?? fallbackUnanswered;

  // Mirror the same fallback logic for `previousQuestion`/`progress`.
  // `progress` is what the SoftSurveyQuestion renders on the top pill,
  // so a missing initial value just shows "0 / N" until the first
  // submitAnswer response comes back.
  const initialPrevious = survey.previousQuestion;
  const initialProgress: SurveyProgress | undefined =
    survey.progress ??
    (initialQuestions.length
      ? {
          completed: initialQuestions.filter(q => !!q.answer).length,
          total: initialQuestions.length,
        }
      : undefined);

  // If the patient already started the survey on a different device
  // and we're resuming mid-flow, skip the intro screen.
  const initialStatus: Status = alreadyComplete
    ? 'complete'
    : !initialCurrent
      ? 'complete'
      : survey.started
        ? 'question'
        : 'intro';

  const [status, setStatus] = useState<Status>(initialStatus);
  const [currentQuestion, setCurrentQuestion] = useState<SurveyQuestion | null>(
    initialCurrent ?? null
  );
  const [previousQuestionId, setPreviousQuestionId] = useState<string | undefined>(initialPrevious);
  const [progress, setProgress] = useState<SurveyProgress | undefined>(initialProgress);
  const [submitting, setSubmitting] = useState(false);
  const [hideSteps, setHideSteps] = useState(false);

  useEffect(() => {
    if (alreadyComplete) {
      setStatus('complete');
    }
  }, [alreadyComplete]);

  const start = () => {
    if (!currentQuestion) {
      setStatus('complete');

      return;
    }

    setHideSteps(true);
    setStatus('question');
    setTimeout(() => setHideSteps(false), 250);
  };

  const onSelect = async (val: SurveyQuestionAnswer) => {
    if (!currentQuestion?.id) return;

    setSubmitting(true);
    setHideSteps(true);

    try {
      const { question: nextQuestion, progress: nextProgress } =
        await requestService.submitSurveyAnswer({
          requestId,
          patientSurveyId,
          patientSurveyQuestionId: currentQuestion.id,
          answer: val,
          validationCode,
          prMap,
          encounterId,
          kiosk,
          remainingSteps,
        });

      setProgress(nextProgress);

      if (nextQuestion?.id) {
        setPreviousQuestionId(currentQuestion.id);
        setCurrentQuestion(nextQuestion);
        // Long surveys leave the patient scrolled past the question
        // text by the time they're picking an answer at the bottom.
        // Snap back to the top so the next question's heading is in
        // view immediately. Same on `goBack` below.
        scrollToTop();
      } else {
        setStatus('complete');
        scrollToTop();
      }
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setSubmitting(false);
      setHideSteps(false);
    }
  };

  const goBack = async () => {
    if (!previousQuestionId) return;

    try {
      const res = await requestService.getPreviousQuestion({
        requestId,
        patientSurveyId,
        questionId: previousQuestionId,
        prMap,
        kiosk,
      });

      setCurrentQuestion(res.question);
      setPreviousQuestionId(res.previousQuestion);
      setProgress(res.progress);
      scrollToTop();
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const scrollToTop = () => {
    // The runner's outer scroll container is the document itself, but
    // some embeds (kiosk frame, parent app) may scroll a different
    // element. Hit both: window for the page and any scrollable
    // ancestor of the survey question that has overflow.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Reeval takes over the entire surface via <ReevalSurvey> (wrapped
  // by InterReeval). It expects the existing patient complaints so it
  // can expand per-complaint follow-up questions before the
  // new-problems prompt. Must come before the CCS branch — a reeval
  // survey could otherwise match `isCCSLike` if the backend ever sent
  // a bodypicker as the start question.
  if (isReeval && startQuestion) {
    return (
      <InterReeval
        requestId={requestId}
        patientSurveyId={patientSurveyId}
        validationCode={validationCode}
        primaryColor={primaryColor}
        language={language}
        gender={gender ?? Genders.notDisclosed}
        age={age ?? 0}
        startQuestion={startQuestion}
        ccsFollowups={ccsFollowups}
        surveyIds={surveyIdsProp}
        prMap={prMap ?? {}}
        encounterId={encounterId}
        kiosk={kiosk}
        complete={onComplete}
        markSurveyComplete={onComplete}
        complaints={complaints ?? []}
        allowAdditionalComplaints={survey.allowAdditionalComplaints !== false}
        customQuestions={survey.behavesAs === 'reevaluation' ? survey.questions : undefined}
        customCCSSurveyId={survey.behavesAs === 'reevaluation' ? survey.id : undefined}
      />
    );
  }

  // CCS takes over the entire survey surface. Skip the soft intro /
  // per-question dispatcher entirely and hand off to the legacy
  // ChiefComplaintSurvey via InterCCS. It owns its own intro screen,
  // its own progress state, and submits the whole survey through
  // `requestService.submitSurvey`.
  if (isCCSLike && startQuestion) {
    return (
      <InterCCS
        requestId={requestId}
        patientSurveyId={patientSurveyId}
        validationCode={validationCode}
        primaryColor={primaryColor}
        language={language}
        gender={gender ?? Genders.notDisclosed}
        age={age ?? 0}
        startQuestion={startQuestion}
        ccsFollowups={ccsFollowups}
        surveyIds={surveyIdsProp}
        prMap={prMap ?? {}}
        encounterId={encounterId}
        kiosk={kiosk}
        complete={onComplete}
        markSurveyComplete={onComplete}
        additionalComplaints={survey.behavesAs === 'additionalComplaintSurvey'}
        abbreviatedCCS={survey.behavesAs === 'abbreviatedCCS'}
        allowAdditionalComplaints={survey.allowAdditionalComplaints !== false}
        customCCSSurveyId={
          survey.behavesAs === 'ccs' || survey.behavesAs === 'abbreviatedCCS'
            ? survey.id
            : undefined
        }
      />
    );
  }

  if (status === 'intro') {
    return (
      <SoftSurveyIntro
        meta={{
          title: survey.title ?? '',
          subtitle: survey.subtitle,
          startMessage: survey.startMessage,
        }}
        language={language}
        primaryColor={primaryColor}
        onStart={start}
      />
    );
  }

  if (status === 'question' && currentQuestion) {
    return (
      <SoftSurveyQuestion
        question={currentQuestion}
        progress={progress}
        language={language}
        primaryColor={primaryColor}
        disabled={submitting}
        hidden={hideSteps}
        showPrevious={!!previousQuestionId}
        onBack={goBack}
        onSelect={onSelect}
      />
    );
  }

  return (
    <SoftSurveyComplete
      language={language}
      primaryColor={primaryColor}
      message={survey.endMessage || (language === 'es' ? 'Gracias.' : 'Thank you.')}
      onExit={onComplete}
      exitLabel={language === 'es' ? 'Continuar' : 'Continue'}
    />
  );
};

export default StepSurvey;
